import React, { CSSProperties } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { isServer, portalRootId } from '@/helpers';
import styles from './Drawer.module.scss';
import { DrawerBody, DrawerBodyProps } from './DrawerBody';
import { DrawerFooter, DrawerFooterProps } from './DrawerFooter';
import { DrawerHeader, DrawerHeaderProps } from './DrawerHeader';
import { DrawerLevel, DrawerLevelProps } from './DrawerLevel';
import { DrawerChildrenType } from './types';

type CssUnits = 'px' | 'vh' | 'vw';
type SupportedCssUnits = `${number}${CssUnits}`;

interface DrawerProps {
	id?: string;
	children: DrawerChildrenType;
	drawerRef: React.RefObject<HTMLDivElement | HTMLDialogElement>;
	direction?: 'Left' | 'Right' | 'Top' | 'Bottom';
	width?: SupportedCssUnits;
	desktopWidth?: SupportedCssUnits;
	height?: SupportedCssUnits;
	desktopHeight?: SupportedCssUnits;
	isOpen?: boolean;
}

/**
 * Generic Drawer component, used for toggling off-screen, single- or multi-"page" menu.
 * Used e.g. for mobile menu or mobile filter menu. This component cares about
 * the slide-in and DrawerLevel-switching behavior, provides support for layout of
 * DrawerHeader, DrawerBody, DrawerFooter, but is DrawerBody content agnostic.
 */
export const Drawer: React.FunctionComponent<DrawerProps> & {
	Level: React.FC<DrawerLevelProps>;
	Body: React.FC<DrawerBodyProps>;
	Header: React.FC<DrawerHeaderProps>;
	Footer: React.FC<DrawerFooterProps>;
} = ({
	id,
	children,
	drawerRef,
	direction = 'Left',
	height,
	desktopHeight,
	width,
	desktopWidth = '375px',
	isOpen,
}: DrawerProps) => {
	const renderDrawer = (): React.ReactNode => (
		<aside
			className={classNames(styles.wrapper, styles[`from${direction}`], {
				[styles.isOpen]: isOpen,
			})}
			style={
				{
					'--height': height,
					'--width': width,
					'--desktop-height': desktopHeight,
					'--desktop-width': desktopWidth,
				} as CSSProperties
			}
			id={id}
			ref={drawerRef}
		>
			<Drawer.Level isActive={true}>{children}</Drawer.Level>
		</aside>
	);

	if (isServer()) return <>{renderDrawer()}</>;

	const drawerElement: HTMLElement | null = document.getElementById(portalRootId);

	return ReactDOM.createPortal(<>{renderDrawer()}</>, drawerElement || document.body);
};

Drawer.Level = DrawerLevel;
Drawer.Body = DrawerBody;
Drawer.Header = DrawerHeader;
Drawer.Footer = DrawerFooter;
